import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    MessageSquare,
    Plus,
    Trash2,
    Edit,
    Power,
    PowerOff,
    ExternalLink,
    Smartphone,
    Clock,
    Users,
    AlertTriangle,
    Image,
    Link,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import {
    getPopups,
    createPopup,
    updatePopup,
    togglePopupActive,
    deletePopup
} from '../utils/api';
import toast from 'react-hot-toast';

const defaultPopup = {
    title: '',
    body: '',
    image: '',
    primaryButtonText: 'OK',
    primaryButtonAction: { type: 'dismiss', value: '' },
    secondaryButtonText: '',
    isForced: false,
    targeting: {
        platforms: [],
        minAppVersion: '',
        maxAppVersion: '',
        accountTypes: [],
        specificUserIds: [],
        excludeUserIds: []
    },
    startsAt: '',
    endsAt: '',
    priority: 0
};

export default function PopupManager() {
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [editingPopup, setEditingPopup] = useState(null);
    const [formData, setFormData] = useState(defaultPopup);
    const [expandedPopup, setExpandedPopup] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    // Fetch all popups
    const { data: popupsData, isLoading } = useQuery({
        queryKey: ['popups'],
        queryFn: () => getPopups({ includeInactive: 'true', limit: 50 }),
        staleTime: 30000,
    });

    // Create popup mutation
    const createMutation = useMutation({
        mutationFn: createPopup,
        onSuccess: () => {
            queryClient.invalidateQueries(['popups']);
            toast.success('Popup created');
            closeModal();
        },
        onError: (error) => toast.error(error.message),
    });

    // Update popup mutation
    const updateMutation = useMutation({
        mutationFn: ({ popupId, data }) => updatePopup(popupId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['popups']);
            toast.success('Popup updated');
            closeModal();
        },
        onError: (error) => toast.error(error.message),
    });

    // Toggle active mutation
    const toggleMutation = useMutation({
        mutationFn: ({ popupId, isActive }) => togglePopupActive(popupId, isActive),
        onSuccess: () => {
            queryClient.invalidateQueries(['popups']);
            toast.success('Popup status updated');
        },
        onError: (error) => toast.error(error.message),
    });

    // Delete popup mutation
    const deleteMutation = useMutation({
        mutationFn: deletePopup,
        onSuccess: () => {
            queryClient.invalidateQueries(['popups']);
            toast.success('Popup deleted');
            setShowDeleteConfirm(null);
        },
        onError: (error) => toast.error(error.message),
    });

    const openCreateModal = () => {
        setEditingPopup(null);
        setFormData(defaultPopup);
        setShowModal(true);
    };

    const openEditModal = (popup) => {
        setEditingPopup(popup);
        setFormData({
            title: popup.title || '',
            body: popup.body || '',
            image: popup.image || '',
            primaryButtonText: popup.primaryButtonText || 'OK',
            primaryButtonAction: popup.primaryButtonAction || { type: 'dismiss', value: '' },
            secondaryButtonText: popup.secondaryButtonText || '',
            isForced: popup.isForced || false,
            targeting: popup.targeting || defaultPopup.targeting,
            startsAt: popup.startsAt ? new Date(popup.startsAt).toISOString().slice(0, 16) : '',
            endsAt: popup.endsAt ? new Date(popup.endsAt).toISOString().slice(0, 16) : '',
            priority: popup.priority || 0
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPopup(null);
        setFormData(defaultPopup);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const data = {
            ...formData,
            startsAt: formData.startsAt ? new Date(formData.startsAt).getTime() : undefined,
            endsAt: formData.endsAt ? new Date(formData.endsAt).getTime() : undefined,
            targeting: {
                ...formData.targeting,
                platforms: formData.targeting.platforms.length ? formData.targeting.platforms : undefined,
                minAppVersion: formData.targeting.minAppVersion || undefined,
                maxAppVersion: formData.targeting.maxAppVersion || undefined,
                accountTypes: formData.targeting.accountTypes.length ? formData.targeting.accountTypes : undefined,
            }
        };

        if (editingPopup) {
            updateMutation.mutate({ popupId: editingPopup._id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const popups = popupsData?.popups || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <MessageSquare className="w-7 h-7" />
                        System Popups
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Manage announcements, force updates, and targeted messages
                    </p>
                </div>
                <Button onClick={openCreateModal} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Create Popup
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                    <div className="text-sm text-gray-500">Total Popups</div>
                    <div className="text-2xl font-bold">{popups.length}</div>
                </Card>
                <Card className="p-4">
                    <div className="text-sm text-gray-500">Active</div>
                    <div className="text-2xl font-bold text-green-600">
                        {popups.filter(p => p.isActive).length}
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="text-sm text-gray-500">Forced</div>
                    <div className="text-2xl font-bold text-red-600">
                        {popups.filter(p => p.isForced && p.isActive).length}
                    </div>
                </Card>
            </div>

            {/* Popups List */}
            <Card>
                <div className="p-4 border-b border-gray-200">
                    <h2 className="font-semibold">All Popups</h2>
                </div>

                {isLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : popups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No popups created yet. Create your first popup!
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {popups.map(popup => (
                            <PopupRow
                                key={popup._id}
                                popup={popup}
                                isExpanded={expandedPopup === popup._id}
                                onToggleExpand={() => setExpandedPopup(
                                    expandedPopup === popup._id ? null : popup._id
                                )}
                                onEdit={() => openEditModal(popup)}
                                onToggleActive={(isActive) =>
                                    toggleMutation.mutate({ popupId: popup._id, isActive })
                                }
                                onDelete={() => setShowDeleteConfirm(popup._id)}
                            />
                        ))}
                    </div>
                )}
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={closeModal}
                title={editingPopup ? 'Edit Popup' : 'Create Popup'}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
                    {/* Basic Info */}
                    <div className="space-y-3">
                        <h3 className="font-medium text-gray-700 border-b pb-1">Content</h3>
                        <Input
                            label="Title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                            placeholder="Important Update"
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Body
                            </label>
                            <textarea
                                value={formData.body}
                                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                required
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Describe the announcement or update..."
                            />
                        </div>
                        <Input
                            label="Image URL (optional)"
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                            placeholder="https://..."
                        />
                    </div>

                    {/* Buttons */}
                    <div className="space-y-3">
                        <h3 className="font-medium text-gray-700 border-b pb-1">Buttons</h3>
                        <Input
                            label="Primary Button Text"
                            value={formData.primaryButtonText}
                            onChange={(e) => setFormData({ ...formData, primaryButtonText: e.target.value })}
                            required
                            placeholder="Update Now"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Button Action
                                </label>
                                <select
                                    value={formData.primaryButtonAction.type}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        primaryButtonAction: {
                                            ...formData.primaryButtonAction,
                                            type: e.target.value
                                        }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="dismiss">Dismiss</option>
                                    <option value="url">Open URL</option>
                                    <option value="deeplink">Navigate (Deep Link)</option>
                                </select>
                            </div>
                            {formData.primaryButtonAction.type !== 'dismiss' && (
                                <Input
                                    label={formData.primaryButtonAction.type === 'url' ? 'URL' : 'Route'}
                                    value={formData.primaryButtonAction.value}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        primaryButtonAction: {
                                            ...formData.primaryButtonAction,
                                            value: e.target.value
                                        }
                                    })}
                                    placeholder={formData.primaryButtonAction.type === 'url'
                                        ? 'https://apps.apple.com/...'
                                        : '/(app)/settings'
                                    }
                                />
                            )}
                        </div>
                        <Input
                            label="Secondary Button Text (optional - dismiss button)"
                            value={formData.secondaryButtonText}
                            onChange={(e) => setFormData({ ...formData, secondaryButtonText: e.target.value })}
                            placeholder="Not Now"
                        />
                    </div>

                    {/* Behavior */}
                    <div className="space-y-3">
                        <h3 className="font-medium text-gray-700 border-b pb-1">Behavior</h3>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.isForced}
                                    onChange={(e) => setFormData({ ...formData, isForced: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Forced (blocks app, cannot dismiss)
                                </span>
                            </label>
                            {formData.isForced && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                    ⚠️ Use for force updates only
                                </span>
                            )}
                        </div>
                        <Input
                            label="Priority (higher = shown first)"
                            type="number"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                        />
                    </div>

                    {/* Targeting */}
                    <div className="space-y-3">
                        <h3 className="font-medium text-gray-700 border-b pb-1">Targeting (all conditions must match)</h3>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Platforms
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.targeting.platforms.includes('ios')}
                                        onChange={(e) => {
                                            const platforms = e.target.checked
                                                ? [...formData.targeting.platforms, 'ios']
                                                : formData.targeting.platforms.filter(p => p !== 'ios');
                                            setFormData({
                                                ...formData,
                                                targeting: { ...formData.targeting, platforms }
                                            });
                                        }}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm">iOS</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.targeting.platforms.includes('android')}
                                        onChange={(e) => {
                                            const platforms = e.target.checked
                                                ? [...formData.targeting.platforms, 'android']
                                                : formData.targeting.platforms.filter(p => p !== 'android');
                                            setFormData({
                                                ...formData,
                                                targeting: { ...formData.targeting, platforms }
                                            });
                                        }}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm">Android</span>
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Leave empty to target all platforms</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="Min App Version"
                                value={formData.targeting.minAppVersion}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    targeting: { ...formData.targeting, minAppVersion: e.target.value }
                                })}
                                placeholder="1.0.0"
                            />
                            <Input
                                label="Max App Version"
                                value={formData.targeting.maxAppVersion}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    targeting: { ...formData.targeting, maxAppVersion: e.target.value }
                                })}
                                placeholder="2.0.0"
                            />
                        </div>
                        <p className="text-xs text-gray-500 -mt-2">
                            For force updates: set maxAppVersion to the last outdated version
                        </p>
                    </div>

                    {/* Schedule */}
                    <div className="space-y-3">
                        <h3 className="font-medium text-gray-700 border-b pb-1">Schedule (optional)</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="Start Date"
                                type="datetime-local"
                                value={formData.startsAt}
                                onChange={(e) => setFormData({ ...formData, startsAt: e.target.value })}
                            />
                            <Input
                                label="End Date"
                                type="datetime-local"
                                value={formData.endsAt}
                                onChange={(e) => setFormData({ ...formData, endsAt: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button type="button" variant="secondary" onClick={closeModal}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={createMutation.isPending || updateMutation.isPending}
                        >
                            {editingPopup ? 'Save Changes' : 'Create Popup'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(null)}
                title="Delete Popup"
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-gray-600">
                        Are you sure you want to delete this popup? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => setShowDeleteConfirm(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => deleteMutation.mutate(showDeleteConfirm)}
                            disabled={deleteMutation.isPending}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function PopupRow({ popup, isExpanded, onToggleExpand, onEdit, onToggleActive, onDelete }) {
    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActionLabel = (action) => {
        if (!action) return 'Dismiss';
        switch (action.type) {
            case 'url': return `URL: ${action.value?.slice(0, 30)}...`;
            case 'deeplink': return `Navigate: ${action.value}`;
            default: return 'Dismiss';
        }
    };

    return (
        <div className="p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{popup.title}</h3>
                        {popup.isForced && (
                            <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Forced
                            </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${popup.isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                            {popup.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{popup.body}</p>

                    {/* Quick info */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {popup.targeting?.platforms?.length > 0 && (
                            <span className="flex items-center gap-1">
                                <Smartphone className="w-3 h-3" />
                                {popup.targeting.platforms.join(', ')}
                            </span>
                        )}
                        {(popup.targeting?.minAppVersion || popup.targeting?.maxAppVersion) && (
                            <span>
                                v{popup.targeting.minAppVersion || '*'} - {popup.targeting.maxAppVersion || '*'}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(popup.createdAt)}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={onToggleExpand}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => onToggleActive(!popup.isActive)}
                        className={`p-1.5 rounded ${popup.isActive
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                        title={popup.isActive ? 'Deactivate' : 'Activate'}
                    >
                        {popup.isActive ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={onEdit}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                    >
                        <Edit className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onDelete}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <div className="text-gray-500 text-xs uppercase mb-1">Primary Button</div>
                        <div>{popup.primaryButtonText}</div>
                        <div className="text-gray-500 text-xs">{getActionLabel(popup.primaryButtonAction)}</div>
                    </div>
                    {popup.secondaryButtonText && (
                        <div>
                            <div className="text-gray-500 text-xs uppercase mb-1">Secondary Button</div>
                            <div>{popup.secondaryButtonText}</div>
                        </div>
                    )}
                    {popup.image && (
                        <div>
                            <div className="text-gray-500 text-xs uppercase mb-1">Image</div>
                            <a href={popup.image} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1">
                                <Image className="w-3 h-3" />
                                View Image
                            </a>
                        </div>
                    )}
                    {(popup.startsAt || popup.endsAt) && (
                        <div>
                            <div className="text-gray-500 text-xs uppercase mb-1">Schedule</div>
                            <div>
                                {popup.startsAt ? formatDate(popup.startsAt) : 'Now'} → {popup.endsAt ? formatDate(popup.endsAt) : 'Forever'}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="text-gray-500 text-xs uppercase mb-1">Priority</div>
                        <div>{popup.priority || 0}</div>
                    </div>
                    {popup.createdBy && (
                        <div>
                            <div className="text-gray-500 text-xs uppercase mb-1">Created By</div>
                            <div>{popup.createdBy}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

